import apiClient, { API_PREFIX } from './client';
import { apiLogger } from '../utils/logger';

import type { AppPageSettingsFormValues } from '../pages/settings/types';

export interface AppSettingsResponse {
  settings: AppPageSettingsFormValues;
}

export async function fetchAppSettings(): Promise<AppSettingsResponse> {
  const response = await apiClient.get<{ success: boolean; data: AppPageSettingsFormValues }>(`${API_PREFIX}/admin/settings`);
  return { settings: response.data.data };
}

export async function updateAppSettings(settings: AppPageSettingsFormValues): Promise<AppSettingsResponse> {
  // DEBUG: Log what we're sending
  apiLogger.debug('[updateAppSettings] Sending to API:', { settingsKeys: Object.keys(settings) });

  // Use longer timeout for settings update (may include S3 uploads for branding images)
  const response = await apiClient.put<{ success: boolean; data: AppPageSettingsFormValues; message: string }>(
    `${API_PREFIX}/admin/settings`,
    { settings },
    { timeout: 60000 }  // 60 seconds timeout for settings with image uploads
  );
  apiLogger.debug('[updateAppSettings] API response:', { success: response.data.success });
  return { settings: response.data.data };
}

export async function fetchMonitoringOverview(
  period?: 'today' | '7days' | '30days' | 'custom',
  startDate?: string,
  endDate?: string
): Promise<MonitoringOverview> {
  const params: any = {};
  if (period) params.period = period;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  const response = await apiClient.get<{ overview: MonitoringOverview }>(`${API_PREFIX}/admin/monitoring`, {
    params,
  });
  return response.data.overview;
}

// Application users
export interface ApplicationUser {
  id: string;
  phoneNumber: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  loyaltyLevel: number;
  loyaltyPoints: number;
  walletBalance: number;
  status: string;
  consentPdpa: boolean;
  consentMarketing: boolean;
  lastLogin?: string | null;
  // Suspension tracking
  suspendReason?: string | null;
  suspendReasonDetail?: string | null;
  suspendedBy?: string | null;
  suspendedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminUser {
  id: string;
  username: string;
  email: string;
  role: 'admin' | 'staff';
  status: 'active' | 'inactive';
  lastLoginAt?: string | null;
  createdAt: string;
  updatedAt: string;
  isDefault: boolean;
  temporaryPassword?: string;
}

export interface MonitoringOverview {
  summary: {
    totalOrders: number;
    ordersToday: number;
    revenueToday: number;
    revenue7Days: number;
  };
  users: {
    total: number;
    newToday: number;
    active24h: number;
    loyaltyDistribution: Array<{ level: number; users: number }>;
  };
  payments: {
    byMethod: Array<{ method: string; count: number; total: number }>;
    byStatus: Array<{ status: string; count: number }>;
  };
  wallet: {
    topupCount: number;
    topupAmount: number;
    averageTopup: number;
    spendAmount: number;
  };
  stickers: Record<string, number>;
  topProducts: Array<{ product: string; quantity: number; revenue: number }>;
  topSearches: Array<{ term: string; count: number }> | [];
  customerBehavior: {
    averageOrderValue: number;
    repeatPurchaseRate: number;
    firstTimeVsReturning: {
      firstTime: number;
      returning: number;
    };
    customerLifetimeValue: number;
  };
}

export interface UsersResponse<T> {
  users: T[];
  total?: number;
}

export interface CreateAdminUserPayload {
  username: string;
  email?: string;
  role?: 'admin' | 'staff';
  status?: 'active' | 'inactive';
  password?: string;
}

export interface CreateAppUserPayload {
  phoneNumber: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  loyaltyLevel?: number;
  loyaltyPoints?: number;
  walletBalance?: number;
  status?: string;
  consentPdpa?: boolean;
  consentMarketing?: boolean;
  // Suspension fields (used when status = 'suspended')
  suspendReason?: string;
  suspendReasonDetail?: string;
}

export async function fetchApplicationUsers(params: { search?: string; limit?: number; offset?: number } = {}): Promise<UsersResponse<ApplicationUser>> {
  const response = await apiClient.get<{ data: { users: ApplicationUser[]; pagination: { total: number } } }>(`${API_PREFIX}/admin/users`, {
    params: {
      ...params,
      type: 'application',
    },
  });
  return {
    users: response.data.data.users,
    total: response.data.data.pagination.total,
  };
}

export async function fetchAdminUsers(params: { search?: string; limit?: number; offset?: number } = {}): Promise<UsersResponse<AdminUser>> {
  const response = await apiClient.get<{ data: { users: AdminUser[]; pagination: { total: number } } }>(`${API_PREFIX}/admin/users`, {
    params: {
      ...params,
      type: 'admin',
    },
  });
  return {
    users: response.data.data.users,
    total: response.data.data.pagination.total,
  };
}

export async function createAdminUser(payload: CreateAdminUserPayload): Promise<AdminUser> {
  const response = await apiClient.post<AdminUser>(`${API_PREFIX}/admin/users`, payload, {
    params: { type: 'admin' },
  });
  return response.data;
}

export async function createApplicationUser(payload: CreateAppUserPayload): Promise<{ id: string }> {
  const response = await apiClient.post<{ id: string }>(`${API_PREFIX}/admin/users`, payload, {
    params: { type: 'application' },
  });
  return response.data;
}

export async function updateAdminUser(userId: string, payload: Partial<CreateAdminUserPayload>): Promise<void> {
  await apiClient.patch(`${API_PREFIX}/admin/users/${userId}`, payload, {
    params: { type: 'admin' },
  });
}

export async function updateApplicationUser(userId: string, payload: Partial<CreateAppUserPayload>): Promise<void> {
  await apiClient.patch(`${API_PREFIX}/admin/users/${userId}`, payload, {
    params: { type: 'application' },
  });
}

export async function deleteAdminUser(userId: string): Promise<void> {
  await apiClient.delete(`${API_PREFIX}/admin/users/${userId}`, {
    params: { type: 'admin' },
  });
}

// Backoffice query
export interface BackofficeQueryResult {
  orders: Array<{
    id: string;
    orderNumber: string;
    status: string;
    paymentStatus: string;
    total: number;
    createdAt: string;
    userId: string;
  }>;
  users: ApplicationUser[];
}

export async function queryBackoffice(term: string): Promise<BackofficeQueryResult> {
  const response = await apiClient.get<BackofficeQueryResult>(`${API_PREFIX}/admin/query`, {
    params: { term },
  });
  return response.data;
}

// Audit logs
export type ActionCategory = 'AUTH' | 'ORDER' | 'USER' | 'FINANCE' | 'DATA' | 'EMS' | 'PRODUCT' | 'OTHER';

export interface AuditLog {
  id: string;
  action: string;
  actionCategory?: ActionCategory;
  actorId?: string;
  actorType?: string;
  entityType?: string;
  entityId?: string;
  summary?: string;
  metadata?: Record<string, unknown> | null;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

export async function fetchAuditLogs(params: {
  limit?: number;
  offset?: number;
  actorId?: string;
  action?: string;
  categories?: ActionCategory[];
} = {}): Promise<AuditLog[]> {
  const { categories, ...restParams } = params;
  const queryParams: Record<string, any> = { ...restParams };

  // Convert categories array to comma-separated string for API
  if (categories && categories.length > 0) {
    queryParams.categories = categories.join(',');
  }

  const response = await apiClient.get<{ logs: AuditLog[] }>(`${API_PREFIX}/admin/audit-logs`, {
    params: queryParams,
  });
  return response.data.logs;
}

export async function createAuditLog(payload: {
  action: string;
  actionCategory?: ActionCategory;
  summary?: string;
  entityType?: string;
  entityId?: string;
  metadata?: Record<string, any>;
}): Promise<void> {
  await apiClient.post(`${API_PREFIX}/admin/audit-logs`, payload);
}

// PDPA
export interface PdpaConsent {
  id: string;
  userId?: string;
  userPhone: string;
  consentType: string;
  consentVersion: string;
  consentMethod: string;
  collectedAt: string;
  evidenceUrl?: string;
  evidenceData?: Record<string, unknown> | null;
  createdAt: string;
}

export interface PdpaRequest {
  id: string;
  userId?: string;
  userPhone: string;
  requestType: string;
  requestReason?: string;
  submittedAt: string;
  status: string;
  notes?: string;
  attachments?: string[];
  resolvedAt?: string;
  resolvedBy?: string;
  createdAt: string;
}

export async function fetchPdpaConsents(params: { limit?: number; offset?: number; type?: string; phone?: string } = {}): Promise<PdpaConsent[]> {
  const response = await apiClient.get<{ consents: PdpaConsent[] }>(`${API_PREFIX}/admin/pdpa/consents`, {
    params,
  });
  return response.data.consents;
}

export async function createPdpaConsent(payload: {
  userId?: string;
  userPhone: string;
  consentType: string;
  consentVersion: string;
  consentMethod?: string;
  collectedAt?: string;
  evidenceUrl?: string;
  evidenceData?: Record<string, any>;
}): Promise<{ id: string }> {
  const response = await apiClient.post<{ id: string }>(`${API_PREFIX}/admin/pdpa/consents`, payload);
  return response.data;
}

export async function fetchPdpaRequests(params: { limit?: number; offset?: number; status?: string; phone?: string } = {}): Promise<PdpaRequest[]> {
  const response = await apiClient.get<{ requests: PdpaRequest[] }>(`${API_PREFIX}/admin/pdpa/requests`, {
    params,
  });
  return response.data.requests;
}

export async function createPdpaRequest(payload: {
  userId?: string;
  userPhone: string;
  requestType: string;
  requestReason?: string;
  submittedAt?: string;
  notes?: string;
  attachments?: any[];
}): Promise<{ id: string }> {
  const response = await apiClient.post<{ id: string }>(`${API_PREFIX}/admin/pdpa/requests`, payload);
  return response.data;
}

export async function updatePdpaRequest(requestId: string, payload: Partial<{ status: string; notes: string; attachments: any[] }>): Promise<void> {
  await apiClient.patch(`${API_PREFIX}/admin/pdpa/requests/${requestId}`, payload);
}

export async function createPdpaUpload(payload: { fileName: string; contentType: string }): Promise<{ upload: any; fileUrl: string }> {
  const response = await apiClient.post<{ upload: any; fileUrl: string }>(`${API_PREFIX}/admin/pdpa/uploads`, payload);
  return response.data;
}

// Content management
export interface ContentItem {
  id: string;
  contentType: string;
  title: string;
  description?: string | null;
  data: Record<string, unknown>;
  status: 'draft' | 'preview' | 'published' | 'scheduled';
  version: number;
  scheduledAt?: string | null;
  publishedAt?: string | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContentVersion {
  id: string;
  version: number;
  status: string;
  summary?: string | null;
  data: Record<string, unknown>;
  createdBy?: string | null;
  createdAt: string;
}

export async function fetchContentItems(params: { status?: string; type?: string; limit?: number; offset?: number } = {}): Promise<ContentItem[]> {
  const response = await apiClient.get<{ items: ContentItem[] }>(`${API_PREFIX}/admin/content`, {
    params,
  });
  return response.data.items;
}

export async function createContentItem(payload: {
  contentType: string;
  title: string;
  description?: string;
  data?: Record<string, unknown>;
  status?: 'draft' | 'preview' | 'published' | 'scheduled';
  scheduledAt?: string;
  summary?: string;
}): Promise<{ id: string }> {
  const response = await apiClient.post<{ id: string }>(`${API_PREFIX}/admin/content`, payload);
  return response.data;
}

export async function updateContentItem(contentId: string, payload: Partial<{ title: string; description: string; data: Record<string, unknown>; status: string; scheduledAt: string; newVersionData: Record<string, unknown>; summary: string }>): Promise<void> {
  await apiClient.patch(`${API_PREFIX}/admin/content/${contentId}`, payload);
}

export async function fetchContentVersions(contentId: string): Promise<ContentVersion[]> {
  const response = await apiClient.get<{ versions: ContentVersion[] }>(`${API_PREFIX}/admin/content/${contentId}/versions`);
  return response.data.versions;
}

// ========================================
// Banners Management
// ========================================

export interface BannerImageMetadata {
  thumbnail?: string;
  medium?: string;
  large?: string;
}

// New media types for YouTube video support
export type MediaType = 'image' | 'video';

export interface ImageMediaItem {
  type: 'image';
  thumbnail?: string;
  medium?: string;
  large?: string;
}

export interface VideoMediaItem {
  type: 'video';
  youtubeId: string;
  thumbnail?: string;
}

export type MediaItem = ImageMediaItem | VideoMediaItem;

export interface Banner {
  id: string;
  title: string;
  body: string | null;
  media: MediaItem[];  // New format: array of image or video items
  images: BannerImageMetadata[];  // Legacy: for backward compatibility
  linkUrl: string | null;
  publishedAt: string | null;
  isActive: boolean;
  sortOrder: number;
  metadata: {
    action_links?: Array<{
      image: string | BannerImageMetadata;
      url: string;
    }>;
  };
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BannersResponse {
  banners: Banner[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
  };
}

// Input type for media in create/update requests
export interface MediaInputItem {
  type: 'image' | 'video';
  data: string;  // Base64 for image OR YouTube URL for video
}

export interface CreateBannerPayload {
  title: string;
  body?: string | null;
  media?: MediaInputItem[];  // New format: array of image/video items
  images?: string[];  // Legacy: Base64 encoded images
  linkUrl?: string | null;
  publishedAt?: string | null;
  isActive?: boolean;
  sortOrder?: number;
  action_links?: Array<{ image_base64: string; url: string }>;  // snake_case for backend API
}

export interface UpdateBannerPayload {
  title?: string;
  body?: string | null;
  media?: MediaInputItem[];  // New media items to add
  images?: string[];  // Legacy: Base64 encoded images (will append)
  removeMediaIndexes?: number[];  // Indexes of media to remove (new)
  removeImageIndexes?: number[];  // Legacy: Indexes of images to remove
  linkUrl?: string | null;
  publishedAt?: string | null;
  isActive?: boolean;
  sortOrder?: number;
  action_links?: Array<{ image_base64: string; url: string }>;  // snake_case for backend API
  removeActionLinkIndexes?: number[];
}

export async function fetchBanners(params: { page?: number; perPage?: number; includeInactive?: boolean } = {}): Promise<BannersResponse> {
  const queryParams: Record<string, string> = {};
  if (params.page) queryParams.page = params.page.toString();
  if (params.perPage) queryParams.perPage = params.perPage.toString();
  if (params.includeInactive !== undefined) queryParams.includeInactive = params.includeInactive.toString();

  const response = await apiClient.get<BannersResponse>(`${API_PREFIX}/banners/admin/list`, {
    params: queryParams,
  });
  return response.data;
}

export async function fetchBanner(bannerId: string): Promise<Banner> {
  const response = await apiClient.get<{ banner: Banner }>(`${API_PREFIX}/banners/${bannerId}`);
  return response.data.banner;
}

export async function createBanner(payload: CreateBannerPayload): Promise<Banner> {
  const response = await apiClient.post<{ banner: Banner; message: string }>(`${API_PREFIX}/banners/admin`, payload);
  return response.data.banner;
}

export async function updateBanner(bannerId: string, payload: UpdateBannerPayload): Promise<Banner> {
  const response = await apiClient.put<{ banner: Banner; message: string }>(`${API_PREFIX}/banners/admin/${bannerId}`, payload);
  return response.data.banner;
}

export async function deleteBanner(bannerId: string): Promise<void> {
  await apiClient.delete(`${API_PREFIX}/banners/admin/${bannerId}`);
}

// ========================================
// Maintenance Mode
// ========================================

export interface MaintenanceSettings {
  enabled: boolean;
  message: string;
  offlineMessage: string;
}

export interface UpdateMaintenanceSettingsPayload {
  enabled: boolean;
  message?: string;
  offlineMessage?: string;
  password: string;
}

export async function fetchMaintenanceSettings(): Promise<MaintenanceSettings> {
  const response = await apiClient.get<{ success: boolean; data: MaintenanceSettings }>(
    `${API_PREFIX}/admin/maintenance`
  );
  return response.data.data;
}

export async function updateMaintenanceSettings(
  payload: UpdateMaintenanceSettingsPayload
): Promise<MaintenanceSettings> {
  const response = await apiClient.put<{
    success: boolean;
    data: MaintenanceSettings;
    message: string;
  }>(`${API_PREFIX}/admin/maintenance`, payload);
  return response.data.data;
}

